import { excludePresetsFromLinkCreatorData } from '../../../common/utils/linkhub-data-filter-helper';
import { Redirect as RedirectType } from '../../screens/ScreenLinkHubQuickLinkCreator/steps/RedirectsStep/Redirects.types';
import { Redirect as RedirectEnum } from '../../screens/ScreenLinkHubQuickLinkCreator/steps/RedirectsStep/Redirects.enums';
import {
  ILinkData,
  IQuickLink,
  IQuickLinkAnalyticsTags,
  IQuickLinkRedirects,
  RedirectsClientOverride,
  ISocialMediaTags,
} from '../../../../../shared/types';

export class QuickLinkEditorService {
  private static getTemplateId = (marketingLink: Record<string, any>): string => {
    return marketingLink?.data?.['~quick_link_template_id'];
  };

  private static getDomain = (marketingLink: Record<string, any>): string => {
    if (marketingLink?.domain_alias?.domain.includes('/e')) {
      return marketingLink?.domain_alias?.domain.replace('/e', '');
    }

    return marketingLink?.domain_alias?.domain;
  };

  private static getTitle = (marketingLink: Record<string, any>): string | undefined => {
    return marketingLink?.data?.$marketing_title;
  };

  private static getAlias = (marketingLink: Record<string, any>): string | undefined => {
    return marketingLink?.domain_alias?.alias;
  };

  private static getAnalyticsTags = (marketingLink: Record<string, any>): IQuickLinkAnalyticsTags => {
    const analyticsTags = new Object() as IQuickLinkAnalyticsTags;

    if (marketingLink?.feature) {
      analyticsTags.feature = marketingLink?.feature;
    }

    if (marketingLink?.channel) {
      analyticsTags.channel = marketingLink?.channel;
    }

    if (marketingLink?.campaign) {
      analyticsTags.campaign = marketingLink?.campaign;
    }

    if (marketingLink?.tags) {
      analyticsTags.tags = marketingLink?.tags;
    }

    return analyticsTags;
  };

  private static getRedirectValidType = (redirect: RedirectType): RedirectsClientOverride => {
    if (redirect === RedirectEnum.Deepview) {
      return RedirectsClientOverride.Deepview;
    } else if (redirect === RedirectEnum.Web) {
      return RedirectsClientOverride.WebUrl;
    } else {
      return RedirectsClientOverride.DefaultRedirect;
    }
  };

  private static getRedirects = (marketingLink: Record<string, any>): IQuickLinkRedirects => {
    const redirects: IQuickLinkRedirects = new Object();

    if (marketingLink?.data?.$ios_url) {
      redirects.ios = {
        override: this.getRedirectValidType('web'),
        value: marketingLink?.data?.$ios_url,
      };
    }

    if (marketingLink?.data?.$android_url) {
      redirects.android = {
        override: this.getRedirectValidType('web'),
        value: marketingLink?.data?.$android_url,
      };
    }

    if (marketingLink?.data?.$desktop_url) {
      redirects.desktop = {
        override: this.getRedirectValidType('web'),
        value: marketingLink?.data?.$desktop_url,
      };
    }

    if (marketingLink?.data?.$web_only) {
      redirects.isWebOnly = Boolean(marketingLink?.data?.$web_only);
    }

    return redirects;
  };

  private static getLinkData = (marketingLink: Record<string, any>): ILinkData => {
    const linkDataWithoutPresets: Record<string, any> = excludePresetsFromLinkCreatorData(marketingLink?.data);

    return linkDataWithoutPresets;
  };

  private static getSocialMediaTags = (marketingLink: Record<string, any>): ISocialMediaTags => {
    const socialMediaTags: ISocialMediaTags = new Object();

    if (marketingLink?.data?.$og_title) {
      socialMediaTags.title = marketingLink?.data?.$og_title;
    }

    if (marketingLink?.data?.$og_description) {
      socialMediaTags.description = marketingLink?.data?.$og_description;
    }

    if (marketingLink?.data?.$og_image_url) {
      socialMediaTags.imageUrl = marketingLink?.data?.$og_image_url;
    }

    return socialMediaTags;
  };

  public static getQuickLinkPayload = (marketingLink: Record<string, any>): IQuickLink => {
    return {
      templateId: this.getTemplateId(marketingLink),
      domain: this.getDomain(marketingLink),
      title: this.getTitle(marketingLink) as string,
      alias: this.getAlias(marketingLink),
      analyticsTags: this.getAnalyticsTags(marketingLink),
      redirects: this.getRedirects(marketingLink),
      linkData: this.getLinkData(marketingLink),
      socialMediaTags: this.getSocialMediaTags(marketingLink),
    };
  };
}
